import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import type { AccessDataType } from 'src/views/Atoms/LEIDropdown/types'
import type { DropdownOptionsContextProps, EquitiesContext, InitialContext } from './types'

interface Props {
  children: ReactNode;
}

const ShareHolderDetailsContext = createContext([])
ShareHolderDetailsContext.displayName = 'ShareHolderDetailsContext'
const ShareHolderDetailsUpdateContext = createContext(null)
ShareHolderDetailsUpdateContext.displayName = 'ShareHolderDetailsUpdateContext'

export const useShareHolderDetails = () => {
  return useContext(ShareHolderDetailsContext)
}

export const useShareHolderDetailsUpdate = () => {
  return useContext(ShareHolderDetailsUpdateContext)
}

const MarketsContext = createContext([])
MarketsContext.displayName = 'MarketsContext'
const MarketsUpdateContext = createContext(null)
MarketsUpdateContext.displayName = 'MarketsUpdateContext'

export const useMarkets = () => {
  return useContext(MarketsContext)
}

export const useMarketsUpdate = () => {
  return useContext(MarketsUpdateContext)
}

const DirectorsDetailsContext = createContext([])
DirectorsDetailsContext.displayName = 'DirectorsDetailsContext'
const DirectorsDetailsUpdateContext = createContext(null)
DirectorsDetailsUpdateContext.displayName = 'DirectorsDetailsUpdateContext'

export const useDirectorsDetails = () => {
  return useContext(DirectorsDetailsContext)
}

export const useDirectorsDetailsUpdate = () => {
  return useContext(DirectorsDetailsUpdateContext)
}

const ContactsDetailsContext = createContext([])
ContactsDetailsContext.displayName = 'ContactsDetailsContext'
const ContactsDetailsUpdateContext = createContext(null)
ContactsDetailsUpdateContext.displayName = 'ContactsDetailsUpdateContext'

export const useContactsDetails = () => {
  return useContext(ContactsDetailsContext)
}

export const useContactsDetailsUpdate = () => {
  return useContext(ContactsDetailsUpdateContext)
}

const CountryOfResidencesContext = createContext([])
CountryOfResidencesContext.displayName = 'CountryOfResidencesContext'
const CountryOfResidencesUpdateContext = createContext(null)
CountryOfResidencesUpdateContext.displayName = 'CountryOfResidencesUpdateContext'

export const useCountryOfResidences = () => {
  return useContext(CountryOfResidencesContext)
}

export const useCountryOfResidencesUpdate = () => {
  return useContext(CountryOfResidencesUpdateContext)
}

const ManageTeamContext = createContext([])
ManageTeamContext.displayName = 'ManageTeamContext'
const ManageTeamUpdateContext = createContext(null)
ManageTeamUpdateContext.displayName = 'ManageTeamUpdateContext'

export const useManageTeam = () => {
  return useContext(ManageTeamContext)
}

export const useManageTeamUpdate = () => {
  return useContext(ManageTeamUpdateContext)
}

const ApplicationSelectedContext = createContext(null)
ApplicationSelectedContext.displayName = 'ApplicationSelectedContext'
const ApplicationSelectedUpdateContext = createContext(null)
ApplicationSelectedUpdateContext.displayName = 'ApplicationSelectedUpdateContext'

export const useApplicationSelected = () => {
  return useContext(ApplicationSelectedContext)
}

export const useApplicationSelectedUpdate = () => {
  return useContext(ApplicationSelectedUpdateContext)
}

const ProspectsApplicationsContext = createContext(null)
ProspectsApplicationsContext.displayName = 'ProspectsApplicationsContext'
const ProspectsApplicationsUpdateContext = createContext(null)
ProspectsApplicationsUpdateContext.displayName = 'ProspectsApplicationsUpdateContext'

export const useProspectsApplications = () => {
  return useContext(ProspectsApplicationsContext)
}

export const useProspectsApplicationsUpdate = () => {
  return useContext(ProspectsApplicationsUpdateContext)
}

const ManageTeamDropdownContext = createContext(null)
ManageTeamDropdownContext.displayName = 'ManageTeamDropdownContext'
const ManageTeamDropdownContextUpdateContext = createContext(null)
ManageTeamDropdownContextUpdateContext.displayName = 'ManageTeamDropdownContextUpdateContext'

export const useManageTeamDropdown = () => {
  return useContext(ManageTeamDropdownContext)
}

export const useManageTeamDropdownContextUpdate = () => {
  return useContext(ManageTeamDropdownContextUpdateContext)
}

const DashboardListContext = createContext(null)
DashboardListContext.displayName = 'DashboardListContext'
const DashboardListUpdateContext = createContext(null)
DashboardListUpdateContext.displayName = 'DashboardListUpdateContext'

export const useDashboardList = () => {
  return useContext(DashboardListContext)
}

export const useDashboardListUpdateContext = () => {
  return useContext(DashboardListUpdateContext)
}

const EquitiesListContext = createContext<EquitiesContext>(null)
EquitiesListContext.displayName = 'EquitiesListContext'
const EquitiesListUpdateContext = createContext<React.Dispatch<React.SetStateAction<EquitiesContext>>>(null)
EquitiesListUpdateContext.displayName = 'EquitiesListUpdateContext'

export const useEquitiesListContext = () => {
  return useContext(EquitiesListContext)
}

const AccountsListContext = createContext<Record<'label' | 'accountId', string>[]>(null)
AccountsListContext.displayName = 'AccountsListContext'
const AccountsListUpdateContext = createContext(null)
AccountsListUpdateContext.displayName = 'AccountsListUpdateContext'

export const useAccountsList = () => {
  return useContext(AccountsListContext)
}

export const useAccountsListUpdateContext = () => {
  return useContext(AccountsListUpdateContext)
}

const AccountSelectedContext = createContext<Record<'label' | 'accountId', string>>(null)
AccountSelectedContext.displayName = 'AccountSelectedContext'
const AccountSelectedUpdateContext = createContext(null)
AccountSelectedUpdateContext.displayName = 'AccountSelectedUpdateContext'

export const useAccountSelected = () => {
  return useContext(AccountSelectedContext)
}

export const useAccountSelectedUpdateContext = () => {
  return useContext(AccountSelectedUpdateContext)
}

export type DropdownOption = {
  label: string
  index: number
}

export const useEquitiesListUpdateContext = () => {
  return useContext(EquitiesListUpdateContext)
}

const DropdownOptionsContext = createContext<{ dropdownOptions: DropdownOptionsContextProps, setDropdownOptions?: (options: DropdownOptionsContextProps) => void }>({
  dropdownOptions: { currencies: [], countries: [], countryTelephoneCodes: [] }
})
DropdownOptionsContext.displayName = 'DropdownOptionsContext'

export const useDropdownOptions = () => {
  return useContext(DropdownOptionsContext)
}

const AccessContext = createContext<AccessDataType | null>(null)
AccessContext.displayName = 'AccessContext'
const AccessUpdateContext = createContext<React.Dispatch<React.SetStateAction<AccessDataType | null>> | null>(null)
AccessUpdateContext.displayName = 'AccessUpdateContext'

export const useAccess = () => {
  return useContext(AccessContext)
}

export const useAccessUpdate = () => {
  return useContext(AccessUpdateContext)
}

const InitialContext = createContext<InitialContext | null>(null)
InitialContext.displayName = 'InitialContext'
const InitialUpdateContext = createContext<React.Dispatch<React.SetStateAction<InitialContext | null>> | null>(null)
InitialUpdateContext.displayName = 'InitialUpdateContext'

export const useInitial = () => {
  return useContext(InitialContext)
}

export const useInitialUpdate = () => {
  return useContext(InitialUpdateContext)
}

export const OnboardingContextProvider = (props: Props) => {
  const [shareholderDetails, setShareHolderDetails] = useState([])
  const [directorsDetails, setDirectorsDetails] = useState([])
  const [contactsDetails, setContactsDetails] = useState([])
  const [countryOfResidences, setCountryOfResidences] = useState([])
  const [manageTeam, setManageTeam] = useState([])
  const [prospecstApplications, setProspectsApplications] = useState([])
  const [applicationSelected, setApplicationSelected] = useState(0)
  const [options, setOptions] = useState()
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptionsContextProps>({ currencies: [], countries: [], countryTelephoneCodes: [] })
  const [markets, setMarkets] = useState([])
  const [dashboards, setDashboards] = useState(null)
  const [equities, setEquities] = useState<EquitiesContext>({ selected: '', list: [] })
  const [accounts, setAccounts] = useState<Record<string, string>[]>(null)
  const [accountSelected, setAccountSelected] = useState<Record<string, string>>(null)
  const [access, setAccess] = useState<AccessDataType | null>(null)
  const [initial, setInitial] = useState<InitialContext | null>(null)

  useEffect(() => {
    setShareHolderDetails([])
    setDirectorsDetails([])
    setContactsDetails([])
    setCountryOfResidences([])
  }, [applicationSelected])

  return (
    <ApplicationSelectedContext.Provider value={applicationSelected}>
      <ApplicationSelectedUpdateContext.Provider value={setApplicationSelected}>
        <ProspectsApplicationsContext.Provider value={prospecstApplications}>
          <ProspectsApplicationsUpdateContext.Provider value={setProspectsApplications}>
            <ShareHolderDetailsContext.Provider value={shareholderDetails}>
              <ShareHolderDetailsUpdateContext.Provider value={setShareHolderDetails}>
                <DirectorsDetailsContext.Provider value={directorsDetails}>
                  <DirectorsDetailsUpdateContext.Provider value={setDirectorsDetails}>
                    <ContactsDetailsContext.Provider value={contactsDetails}>
                      <ContactsDetailsUpdateContext.Provider value={setContactsDetails}>
                        <CountryOfResidencesContext.Provider value={countryOfResidences}>
                          <CountryOfResidencesUpdateContext.Provider value={setCountryOfResidences}>
                            <ManageTeamContext.Provider value={manageTeam}>
                              <ManageTeamUpdateContext.Provider value={setManageTeam}>
                                <ManageTeamDropdownContext.Provider value={options}>
                                  <ManageTeamDropdownContextUpdateContext.Provider value={setOptions}>
                                    <DropdownOptionsContext.Provider value={{ dropdownOptions, setDropdownOptions }}>
                                      <MarketsContext.Provider value={markets}>
                                        <MarketsUpdateContext.Provider value={setMarkets}>
                                          <DashboardListContext.Provider value={dashboards}>
                                            <DashboardListUpdateContext.Provider value={setDashboards}>
                                              <EquitiesListContext.Provider value={equities}>
                                                <EquitiesListUpdateContext.Provider value={setEquities}>
                                                  <AccountsListContext.Provider value={accounts}>
                                                    <AccountsListUpdateContext.Provider value={setAccounts}>
                                                      <AccountSelectedContext.Provider value={accountSelected}>
                                                        <AccountSelectedUpdateContext.Provider value={setAccountSelected}>
                                                          <AccessContext.Provider value={access}>
                                                            <AccessUpdateContext.Provider value={setAccess}>
                                                              <InitialContext.Provider value={initial}>
                                                                <InitialUpdateContext.Provider value={setInitial}>
                                                                  {props.children}
                                                                </InitialUpdateContext.Provider>
                                                              </InitialContext.Provider>
                                                            </AccessUpdateContext.Provider>
                                                          </AccessContext.Provider>
                                                        </AccountSelectedUpdateContext.Provider>
                                                      </AccountSelectedContext.Provider>
                                                    </AccountsListUpdateContext.Provider>
                                                  </AccountsListContext.Provider>
                                                </EquitiesListUpdateContext.Provider>
                                              </EquitiesListContext.Provider>
                                            </DashboardListUpdateContext.Provider>
                                          </DashboardListContext.Provider>
                                        </MarketsUpdateContext.Provider>
                                      </MarketsContext.Provider>
                                    </DropdownOptionsContext.Provider>
                                  </ManageTeamDropdownContextUpdateContext.Provider>
                                </ManageTeamDropdownContext.Provider>
                              </ManageTeamUpdateContext.Provider>
                            </ManageTeamContext.Provider>
                          </CountryOfResidencesUpdateContext.Provider>
                        </CountryOfResidencesContext.Provider>
                      </ContactsDetailsUpdateContext.Provider>
                    </ContactsDetailsContext.Provider>
                  </DirectorsDetailsUpdateContext.Provider>
                </DirectorsDetailsContext.Provider>
              </ShareHolderDetailsUpdateContext.Provider>
            </ShareHolderDetailsContext.Provider>
          </ProspectsApplicationsUpdateContext.Provider>
        </ProspectsApplicationsContext.Provider>
      </ApplicationSelectedUpdateContext.Provider>
    </ApplicationSelectedContext.Provider>
  )
}
